import { CheckCircle2, XCircle, Info, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import type { DamuMedConnectionStatus as Status } from '../../types/tenant.types';

const TITLES: Record<Status['status'], string> = {
  ok: 'Подключение к DamuMed работает',
  locked: 'Учётная запись заблокирована в DamuMed',
  invalid_credentials: 'DamuMed не принял логин или пароль',
  error: 'Не удалось подключиться к DamuMed',
  not_configured: 'DamuMed не настроен',
  unknown: 'Подключение не проверялось',
};

function formatTime(iso?: string): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  status: Status;
  /** Повторить вход, несмотря на паузу (учётку уже разблокировали) */
  onForceTest: () => void;
  isTesting: boolean;
}

/**
 * Надпись о состоянии подключения к DamuMed в карточке настроек: результат
 * кнопки «Проверить подключение» или последнее известное состояние.
 */
export function DamuMedConnectionStatus({ status, onForceTest, isTesting }: Props) {
  const isNeutral = status.status === 'unknown' || status.status === 'not_configured';
  const isPaused = Boolean(status.paused_until);

  const tone = status.ok
    ? 'border-green-200 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100 dark:border-green-900'
    : isNeutral
      ? 'border-border bg-muted/40 text-foreground'
      : 'border-red-200 bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100 dark:border-red-900';

  const Icon = status.ok ? CheckCircle2 : isNeutral ? Info : XCircle;
  const checkedAt = formatTime(status.checked_at);
  const pausedUntil = formatTime(status.paused_until);
  const tokenUntil = formatTime(status.token_expires_at);

  return (
    <div role="status" className={`rounded-md border p-3 text-sm ${tone}`}>
      <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="space-y-1 min-w-0">
          <div className="font-medium">{TITLES[status.status]}</div>
          <div className="text-xs">{status.message}</div>

          {status.reason && (
            <div className="text-xs break-words">
              <span className="opacity-75">Ответ DamuMed: </span>
              {status.reason}
            </div>
          )}

          {status.ok && (checkedAt || tokenUntil) && (
            <div className="text-xs opacity-75">
              {checkedAt && <>Вход выполнен {checkedAt}</>}
              {checkedAt && tokenUntil && ' · '}
              {tokenUntil && <>токен действует до {tokenUntil}</>}
            </div>
          )}

          {!status.ok && checkedAt && !isNeutral && (
            <div className="text-xs opacity-75">Попытка входа: {checkedAt}</div>
          )}

          {isPaused && (
            <div className="text-xs">
              Автоматический вход приостановлен до {pausedUntil}, чтобы каждая
              попытка не продлевала блокировку. Пауза снимется сразу после
              сохранения нового логина или пароля.
              {!status.live && ' Сейчас в DamuMed не обращались.'}
            </div>
          )}

          {isPaused && !status.live && (
            <Button
              variant="outline"
              size="sm"
              className="mt-1"
              onClick={onForceTest}
              disabled={isTesting}
            >
              {isTesting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Учётку уже разблокировали — проверить всё равно
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
